import * as THREE from "three";
import {getEnumValues} from "../CBARUtils";
import {Mat, Point} from "mirada";
import {Scalar} from "../components";
import * as gm from "gammacv";

export type Size = {
    width:number
    height:number
}

export type InputShape = {
    width:number,
    height:number,
    channels:number
}

export type ImageBuffer = InputShape & {
    data:ArrayBuffer
}

export const describeImageInput = (image:Mat):InputShape=>{
    return {width:image.cols, height:image.rows, channels:image.channels()}
}

export const serializeImage = (image:Mat):ImageBuffer=>{
    let data = new Uint8Array(image.data);
    //console.log("serialize", data.slice(0, 10));
    return {...describeImageInput(image), data}
}

export const deserializeImage = (buffer:ImageBuffer)=>{
    //console.log("deserialize", buffer.data.slice(0, 10));
    return cv.matFromArray(buffer.height, buffer.width, getMatrixType(buffer.channels), buffer.data);
}

const pattern = /#include <(.*)>/gm;

export function parseIncludes( string:any ){
    function replace( match:any , include:any ){
        const replace = THREE.ShaderChunk[ include ];
        return parseIncludes( replace )
    }
    return string.replace( pattern, replace )
}

function isArrayOfStrings(value: any): boolean {
    return Array.isArray(value) && value.every(item => typeof item === "string");
}

export function confineToEnum<T>(en:any, values:T[]) : T[] {
    const available = getEnumValues(en);
    if (isArrayOfStrings(available) && isArrayOfStrings(values)) {
        const valueStrings = values as unknown[] as string[];
        const matches = available.filter(avail=>valueStrings.find(value=>(avail as string).toLowerCase()==value.toLowerCase()));
        //console.log("available", available, "valueStrings", valueStrings, "matches", matches);
        return matches.map(str=>str as T);
    }
    const restricted = values.filter(value=>available.indexOf(value) >= 0);
    return restricted as T[]
}

export function randomIntFromInterval(min:number, max:number) { // min and max inclusive
    return Math.floor(Math.random() * (max - min + 1) + min);
}

//https://stackoverflow.com/questions/9229645/remove-duplicate-values-from-js-array
export function uniq(a:any) {
    let seen:any = {};
    return a.filter(function(item:any) {
        return seen.hasOwnProperty(item) ? false : (seen[item] = true);
    });
}

export function normalizedToScreen(point2D:THREE.Vector2) : THREE.Vector2 {
    return new THREE.Vector2(point2D.x * 2.0 - 1.0, 1.0 - point2D.y * 2.0)
}

export function normalizePoint(point:Point, within:{width:number, height:number}) : Point {
    return new THREE.Vector2(point.x / within.width, point.y / within.height)
}

export function denormalizePoint(point:Point, within:{width:number, height:number}) : Point {
    return new THREE.Vector2(point.x * within.width, point.y * within.height)
}

/**
 * @author Mikhail Zachepilo <mihailzachepilo@gmail.com>
 */

/**
 * @typedef {number} Ratio - number representation of ratio: width / height
 * @example 4 / 3 = 1.3333333333333333
 */

/**
 * @typedef {{width: number, height: number}} Size
 */

/**
 * @param {Ratio} ratio
 * @param {number} h
 */
export function getWidth(ratio: number, h: number): number {
    return ratio * h;
}

/**
 * @param {Ratio} ratio
 * @param {number} w
 */
export function getHeight(ratio: number, w: number): number {
    return w / ratio;
}

/**
 *
 * @param {Ratio} ratio
 * @param {number} maxWidth
 * @param {number} [maxHeight]
 * @return {Size}
 */
export function getMaxAvailableSize(ratio: number, maxWidth: number, maxHeight: number) {
    if (maxWidth) {
        const height = getHeight(ratio, maxWidth);

        if (height <= maxHeight) {
            return {
                height,
                width: maxWidth,
            };
        }
    }

    return {
        width: getWidth(ratio, maxHeight),
        height: maxHeight,
    };
}

/**
 *
 * @param {Ratio} ratio
 * @param {number} minWidth
 * @param {number} [minHeight]
 * @return {Size}
 */
export function getMinAvailableSize(ratio: number, minWidth: number, minHeight: number) {
    if (minWidth) {
        const height = getHeight(ratio, minWidth);

        if (height > minHeight) {
            return {
                height,
                width: minHeight,
            };
        }
    }

    return {
        width: getWidth(ratio, minHeight),
        height: minHeight,
    };
}

type TDeviceType = 'desktop' | 'tablet' | 'mobile';

export interface IDeviceInfo {
    type: TDeviceType
    height: number;
    width: number;
}

export type DeferredExecutor<T> = {
    resolve:(value: T | PromiseLike<T>) => void,
    reject:(reason: any) => void
}

export const getDeviceInfo = (): IDeviceInfo => {
    const { innerWidth, innerHeight } = window;
    let type: TDeviceType = 'desktop';

    if (innerWidth <= 1024 && innerWidth > 768 && innerHeight > 375) {
        type = 'tablet';
    } else if (innerWidth <= 768 || (innerWidth <= 812 && innerHeight <= 375)) {
        type = 'mobile';
    }

    return {
        type,
        width: innerWidth,
        height: innerHeight,
    };
};

export const shuffle = (array:any[]) => {
    let currentIndex = array.length,  randomIndex;

    // While there remain elements to shuffle...
    while (0 !== currentIndex) {

        // Pick a remaining element...
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex--;

        // And swap it with the current element.
        [array[currentIndex], array[randomIndex]] = [
            array[randomIndex], array[currentIndex]];
    }

    return array;
}

const CV_CN_SHIFT = 3;
const CV_DEPTH_MAX = (1 << CV_CN_SHIFT)
const CV_MAT_DEPTH_MASK = (CV_DEPTH_MAX - 1);

export const cvType2String = (type:number) => {
    let r:string;

    let depth = type & CV_MAT_DEPTH_MASK;
    let chans = 1 + (type >> CV_CN_SHIFT);

    switch ( depth ) {
        case cv.CV_8U:  r = "8U"; break;
        case cv.CV_8S:  r = "8S"; break;
        case cv.CV_16U: r = "16U"; break;
        case cv.CV_16S: r = "16S"; break;
        case cv.CV_32S: r = "32S"; break;
        case cv.CV_32F: r = "32F"; break;
        case cv.CV_64F: r = "64F"; break;
        default:     r = "User"; break;
    }

    r += "C";
    r += (chans);

    return r;
}

export const INT_MAX = 2147483647

export const toRadians = (degrees:number)=>{return degrees * Math.PI / 180; }

export const toDegrees = (radians:number)=>{return radians * 180 / Math.PI; }

/*
ChatGPT output, lol
The formula for converting from horizontal field of view (HFOV) to vertical field of view (VFOV) is:
VFOV = 2 * atan(tan(HFOV/2) / aspect ratio),
where aspect ratio is the ratio of the width of the image to its height.
 */
export const hfovToVFov = (hfovDegrees:number, aspectRatio:number)=>{
    return toDegrees(2 * Math.atan( Math.tan( toRadians(hfovDegrees) / 2) / aspectRatio ));
}

/*
 The formula for converting from vertical field of view (VFOV) to horizontal field of view (HFOV) is:
 HFOV = 2 * atan(tan(VFOV/2) * aspect ratio),
 where aspect ratio is the ratio of the width of the image to its height.
*/
export const vfovToHFov = (vfovDegrees:number, aspectRatio:number)=>{
    return toDegrees(2 * Math.atan( Math.tan( toRadians(vfovDegrees) / 2) * aspectRatio ));
}

export const pointsToContour = (points:Point[]) => {
    const contour = new cv.Mat(points.length, 1, cv.CV_32SC2);
    for (let i=0; i<points.length; i++) {
        contour.data32S[i*2] = points[i].x;
        contour.data32S[i*2+1] = points[i].y;
    }
    return contour;
}

export const drawLines = (img:Mat, lines:gm.Line[]) => {
    const contour = new cv.Mat();
    const cont = new cv.MatVector();
    cont.push_back(contour);

    cv.drawContours(img, cont, 0, [1, 0, 0, 0], cv.FILLED);
    cont.delete();
}

export class Bounds {
    constructor(public x1:number, public y1:number, public x2:number, public y2:number) {

    }

    public get width() {
        return this.x2 - this.x1;
    }

    public get height() {
        return this.y2 - this.y1;
    }

    public get area() {
        return this.width * this.height;
    }
}

export const getCanvasMat = (canvas:HTMLCanvasElement, bounds:Bounds) => {
    const sx = canvas.width * bounds.x1;
    const sy = canvas.height * bounds.y1;
    const sw = canvas.width * (bounds.x2 - bounds.x1);
    const sh = canvas.height * (bounds.y2 - bounds.y1);

    const context = canvas.getContext("2d");
    if (context) {
        return cv.matFromImageData(canvas.getContext("2d")!.getImageData(sx, sy, sw, sh));
    }
}

export const overlayMask = (_mask:Mat, img:Mat, value=0, alpha:number|undefined=undefined) => {

    let mask = new cv.Mat();
    if (_mask.depth() !== cv.CV_8U) {
        _mask.convertTo(mask, cv.CV_8U);
    } else {
        mask = _mask;
    }
    const hsv = new cv.Mat();
    let rgb:Mat = new cv.Mat();
    if (img.channels() == 3) {
        rgb = img.clone();
    } else {
        cv.cvtColor(img, rgb, cv.COLOR_GRAY2RGB);
    }
    cv.cvtColor(rgb, hsv, cv.COLOR_RGB2HSV);

    const hsvChannels = new cv.MatVector();
    cv.split(hsv, hsvChannels);

    const hueChannel = hsvChannels.get(0);
    hueChannel.setTo([255,255,255,255], mask);
    hsvChannels.set(0, hueChannel);
    hueChannel.delete();

    const satChannel = hsvChannels.get(1);
    if (value) {
        satChannel.setTo([value,value,value,255], mask);
    } else {
        mask.copyTo(satChannel);
    }

    if (mask != _mask) {
        mask.delete();
    }
    hsvChannels.set(1, satChannel);
    satChannel.delete();
    cv.merge(hsvChannels, hsv);
    hsvChannels.delete();

    if (alpha) {
        let rgbResult = new cv.Mat();
        cv.cvtColor(hsv, rgbResult, cv.COLOR_HSV2RGB);

        const beta = ( 1.0 - alpha );
        cv.addWeighted( rgb, alpha, rgbResult, beta, 0.0, rgb);
        rgbResult.delete();
    } else {
        cv.cvtColor(hsv, rgb, cv.COLOR_HSV2RGB);
    }
    hsv.delete();

    return rgb;
}

export const extractChannel = (image:Mat, index:number) => {
    const channels = new cv.MatVector();
    cv.split(image, channels);
    const channel = channels.get(index).clone();
    channels.delete();
    return channel
}

export const getTestCube = () =>{
    const piece = new THREE.BoxGeometry(1, 1, 1).toNonIndexed();
    const material = new THREE.MeshBasicMaterial({
        vertexColors: true
    });
    const positionAttribute = piece.getAttribute('position');
    const colors = [];

    const color = new THREE.Color();

    for (let i = 0; i < positionAttribute.count; i += 6) {

        color.setHex(0xffffff * Math.random());

        colors.push(color.r, color.g, color.b);
        colors.push(color.r, color.g, color.b);
        colors.push(color.r, color.g, color.b);

        colors.push(color.r, color.g, color.b);
        colors.push(color.r, color.g, color.b);
        colors.push(color.r, color.g, color.b);
    } // for

    // define the new attribute
    piece.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    return new THREE.Mesh(piece, material);
}

export const placeInFrontOfCamera = (camera:THREE.Camera, obj:THREE.Object3D, dist:number) => {
    // fixed distance from camera to the object
    let zCamVec = new THREE.Vector3(0,0, -dist);
    let position = camera.localToWorld(zCamVec);

    obj.position.set(position.x, position.y, position.z);
    obj.lookAt(camera.position);
}

export const worldPointFromScreenPoint = (screenPoint:THREE.Vector2, camera:THREE.Camera) => {

    let worldPoint = new THREE.Vector3();
    worldPoint.x = screenPoint.x;
    worldPoint.y = screenPoint.y;
    worldPoint.z = 0;
    worldPoint.unproject( camera );
    return worldPoint;

}

export const getIntensity = (color:Scalar|Float64Array|number) => {
    if (typeof color === 'number') {
        return color
    }
    return color.length > 1 ? color[1] : color[0];
}

export const getMatrixType = (channels:number) => {
    switch (channels) {
        case 1:
            return cv.CV_8UC1;
        case 2:
            return cv.CV_8UC2;
        case 3:
            return cv.CV_8UC3;
        case 4:
            return cv.CV_8UC4;
    }
    return cv.CV_8U;
}

export const canvasCreate = (options?:{width?:number, height?:number, cpu?:boolean})=>{
    const canvas = gm.canvasCreate(options?.width ? options?.width : 1024, options?.height ? options?.height : 1024);
    const cpu = options ? options.cpu : false;
    const context = canvas.getContext('2d', {willReadFrequently: cpu});//willReadFrequently makes this CPU based
    if (context) context.imageSmoothingEnabled = true;
    return canvas as HTMLCanvasElement;
}

export const matRetainCount = (obj:Mat) => {
    const jsObject = obj as any;
    return jsObject.hasOwnProperty("retainCount") ? jsObject.retainCount : undefined;
}

export const matRetain = (obj:Mat, trackMemoryUsage=false) => {
    const jsObject = obj as any;
    jsObject.retainCount = jsObject.hasOwnProperty("retainCount") ? jsObject.retainCount + 1 : 1;
    if (trackMemoryUsage) {
        jsObject.trackMemoryUsage = true;
        console.log("matRetain.retainCount", jsObject.retainCount);
    }
    return obj;
}

export const matRelease = (obj:Mat) => {
    const jsObject = obj as any;
    const trackMemoryUsage = jsObject.hasOwnProperty("trackMemoryUsage");

    if (jsObject.hasOwnProperty("retainCount")) {
        jsObject.retainCount -= 1;
        if (trackMemoryUsage) {
            console.log("matRelease.retainCount", jsObject.retainCount);
        }
        if (jsObject.retainCount > 0) return;
    }

    if (trackMemoryUsage) {
        console.log("matrix retain count zero, deleting");
    }
    obj.delete();
}

export const kmeansBestLabel = (labels:Mat) => {
    const counts = new Uint8Array(labels.rows);
    for (let i=0; i<labels.rows; i++) {
        counts[labels.data32S[i]] += 1;
    }
    const bestIndex = counts.reduce((iMax, x, i, arr) => x > arr[iMax] ? i : iMax, 0);
    return bestIndex;
}

export const kmeansPoints = (points:Point[], k:number, iterations=Math.ceil(Math.min(points.length / 2, 10)), eps=1.0 , attempts=1) => {

    const labels= new cv.Mat();
    const centers = new cv.Mat();
    const termCriteria = new cv.TermCriteria(cv.TermCriteria_COUNT + cv.TermCriteria_EPS, iterations, eps);
    let sample = new cv.Mat(points.length, 2, cv.CV_32F);
    points.forEach((p, index)=>{
        sample.data32F[index * 2] = p.x;
        sample.data32F[index * 2 + 1] = p.y;
    })
    cv.kmeans(sample, k, labels, termCriteria, attempts, 0, centers);

    return [labels, centers];
}

export const fastDilateErode = (img:Mat, operation=cv.dilate, kernelSize=5, scale=0.5, iterations=1, maintainSize=true, interpolation=cv.INTER_NEAREST)=>{

    const operatingSize = new cv.Size(Math.ceil(img.cols * scale), Math.ceil(img.rows * scale));
    const dst = new cv.Mat();
    cv.resize(img, dst, operatingSize, interpolation);

    const kernel = cv.getStructuringElement(cv.MORPH_ELLIPSE, new cv.Size(kernelSize,kernelSize));

    operation(dst, dst, kernel, {x:0, y:0}, iterations);

    if (maintainSize) {
        cv.resize(dst, dst, img.size(), interpolation);
    }

    return dst;
}